"use client";

import type { ReactNode } from "react";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { cn } from "@lib/utils";

interface SectionDetailItemBase {
  id: string;
  label: ReactNode;
  icon: ReactNode;
}

export type SectionDetailItem =
  | (SectionDetailItemBase & { href: string; onSelect?: never })
  | (SectionDetailItemBase & { href?: never; onSelect: () => void });

interface SectionDetailLayoutProps {
  items: SectionDetailItem[];
  activeId: string;
  children: ReactNode;
}

export const SectionDetailLayout = ({ items, activeId, children }: SectionDetailLayoutProps) => {
  const t = useTranslations("common.components.page.sectionDetailLayout");

  return (
    <div className="grid w-full grid-cols-1 gap-6 md:grid-cols-[14rem_minmax(0,1fr)]">
      <nav aria-label={t("navigationLabel")} className="min-w-0">
        <ul className="flex flex-col gap-1">
          {items.map((item) => {
            const isActive = item.id === activeId;
            const className = cn(
              "flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none",
              "hover:bg-muted/50 hover:text-foreground",
              isActive ? "bg-muted font-medium text-foreground" : "text-muted-foreground",
            );
            const content = (
              <>
                <span
                  aria-hidden="true"
                  className="inline-flex size-5 shrink-0 items-center justify-center [&_svg]:size-5"
                >
                  {item.icon}
                </span>
                <span>{item.label}</span>
              </>
            );

            return (
              <li key={item.id}>
                {typeof item.href === "string" ? (
                  <Link
                    href={item.href}
                    className={className}
                    aria-current={isActive ? "page" : undefined}
                    data-active={isActive}
                  >
                    {content}
                  </Link>
                ) : (
                  <button
                    type="button"
                    onClick={item.onSelect}
                    className={className}
                    aria-current={isActive ? "true" : undefined}
                    data-active={isActive}
                  >
                    {content}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      </nav>
      <div className="min-w-0">{children}</div>
    </div>
  );
};
